import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { formatRelativeTime, getCommunityTabs, getGenreDisplayLabel } from './communityUtils';
import { getAchievement } from '../../../shared/achievements';
import { getAchievementTitle } from '../../utils/i18nHelpers';

export function CommunityShell({ activeTab, composerBar, children }) {
  const { t } = useTranslation();
  const viewerState = useQuery(api.community.getViewerState, {});
  const handle = viewerState?.profile?.handle;
  const tabs = getCommunityTabs(t);

  return (
    <div className="community-shell">
      <div className="community-container">
        <header className="community-header">
          <div className="community-header-left">
            <h1>{t("community.title")}</h1>
            <p className="community-subtitle">{t("community.subtitle")}</p>
          </div>
          <div className="community-header-actions">
            <Link className="btn btn-ghost btn-sm" to="/projects">{t("common.projects")}</Link>
            <Link className="btn btn-secondary btn-sm" to={handle ? `/community/${handle}` : '/profile'}>{t("profile.myPage")}</Link>
          </div>
        </header>

        {composerBar}

        <nav className="community-tabs">
          {tabs.map((tab) => (
            <NavLink
              key={tab.key}
              className={({ isActive }) =>
                `community-tab ${isActive || activeTab === tab.key ? 'active' : ''}`
              }
              to={tab.to}
              end={tab.key === 'home'}
            >
              {tab.label}
            </NavLink>
          ))}
        </nav>

        <main className="community-feed">{children}</main>
      </div>
    </div>
  );
}

function AvatarBlock({ displayName, avatar, className = '' }) {
  const initial = displayName?.charAt(0) || 'A';
  if (avatar) {
    return (
      <div className={`community-avatar ${className}`}>
        <img src={avatar} alt={displayName} />
      </div>
    );
  }
  return <div className={`community-avatar ${className}`}>{initial}</div>;
}

export function CommunityAuthorCard({ author, onFollow, isFollowing }) {
  const { t } = useTranslation();
  return (
    <div className="community-author-card">
      <div className="community-author-top">
        <AvatarBlock displayName={author.displayName} avatar={author.avatar} />
        <div>
          <Link className="community-author-name" to={`/community/${author.handle}`}>
            {author.displayName}
          </Link>
          <div className="community-author-handle">@{author.handle}</div>
        </div>
        {onFollow ? (
          <button
            className={`btn btn-sm ${isFollowing ? 'btn-secondary' : 'btn-primary'}`}
            onClick={() => onFollow(author.userId)}
          >
            {isFollowing ? t('profile.followingLabel') : t('profile.follow')}
          </button>
        ) : null}
      </div>
      {author.bio ? <p className="community-author-bio">{author.bio}</p> : null}
      <div className="community-meta-row">
        <span>{author.followerCount} {t('profile.followers')}</span>
        <span>{author.postCount} {t('profile.posts')}</span>
        {author.genreTags?.length ? (
          <span className="community-genre-dot">{getGenreDisplayLabel(t, author.genreTags[0])}</span>
        ) : null}
      </div>
    </div>
  );
}

function PollBlock({ poll, onVote, disabled }) {
  const { t } = useTranslation();
  const [submittingId, setSubmittingId] = useState(null);
  const totalVotes = poll.totalVotes || 0;

  return (
    <div className="community-poll">
      <div className="community-poll-options">
        {poll.options.map((option) => {
          const pct = totalVotes > 0 ? Math.round((option.voteCount / totalVotes) * 100) : 0;
          const active = poll.viewerVoteOptionId === option.id;
          const showResults = poll.hasVoted || poll.isClosed;
          return (
            <button
              key={option.id}
              className={`community-poll-option ${active ? 'active' : ''} ${showResults ? 'voted' : ''}`}
              disabled={disabled || poll.hasVoted || poll.isClosed || submittingId === option.id}
              onClick={async () => {
                if (!onVote) return;
                setSubmittingId(option.id);
                try {
                  await onVote(option.id);
                } finally {
                  setSubmittingId(null);
                }
              }}
            >
              <span className="poll-option-label">{option.label}</span>
              {showResults && <span className="poll-option-pct">{pct}%</span>}
              {showResults && (
                <span className="community-poll-bar" style={{ width: `${pct}%` }} />
              )}
            </button>
          );
        })}
      </div>
      <div className="community-poll-footer">
        {totalVotes} {totalVotes === 1 ? t('community.voteCount', {count: 1}) : t('community.voteCount_plural', {count: totalVotes})}
        {!poll.hasVoted && !poll.isClosed && ' · ' + t('community.voteToSeeResults')}
      </div>
    </div>
  );
}

/* ── Inline Comments Section ── */
function InlineComments({ postId, viewerProfile }) {
  const { t } = useTranslation();
  const comments = useQuery(api.community.listComments, { postId });
  const addComment = useMutation(api.community.addComment);
  const deleteComment = useMutation(api.community.deleteComment);
  const [body, setBody] = useState('');
  const [posting, setPosting] = useState(false);

  const handleSubmit = async () => {
    if (!body.trim()) return;
    setPosting(true);
    try {
      await addComment({ postId, body });
      setBody('');
    } catch (e) {
      console.error(e);
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="inline-comments">
      {(comments || []).map((c) => (
        <div key={c._id} className="inline-comment">
          <AvatarBlock displayName={c.author.displayName} avatar={c.author.avatar} className="tiny" />
          <div className="inline-comment-content">
            <div className="inline-comment-header">
              <Link className="inline-comment-author" to={`/community/${c.author.handle}`}>
                {c.author.displayName}
              </Link>
              <span className="inline-comment-time">{formatRelativeTime(t, c.createdAt)}</span>
              {c.canDelete && (
                <button
                  className="inline-comment-delete"
                  onClick={() => deleteComment({ commentId: c._id })}
                >
                  ×
                </button>
              )}
            </div>
            <p className="inline-comment-body">{c.body}</p>
          </div>
        </div>
      ))}

      {viewerProfile ? (
        <div className="inline-comment-form">
          <input
            className="inline-comment-input"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t("community.writeAComment")}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSubmit()}
          />
          <button
            className="btn btn-primary btn-sm"
            onClick={handleSubmit}
            disabled={!body.trim() || posting}
          >
            {posting ? '...' : t('common.post')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function CommunityPostCard({
  post,
  viewerProfile,
  onToggleSave,
  onToggleLike,
  onToggleFollow,
  onVotePoll,
  hideActions = false,
}) {
  const { t } = useTranslation();
  const [revealSpoiler, setRevealSpoiler] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const excerptText = post.spoiler && !revealSpoiler ? t('community.spoilerHidden') : post.excerpt?.excerptText;

  const postTypeLabel = post.type === 'excerpt'
    ? `📖 ${t('community.postTypes.excerpt')}`
    : post.type === 'poll'
      ? `📊 ${t('community.postTypes.poll')}`
      : post.type === 'audio'
        ? `🎧 ${t('community.postTypes.audio')}`
        : null;
  const linkedTitle = post.linkedProject?.title;
  const isOwnPost = viewerProfile?.userId === post.author.userId;
  const isFollowing = post.viewer?.isFollowingAuthor;

  return (
    <article className="community-post-card">
      <div className="community-post-top">
        <Link to={`/community/${post.author.handle}`}>
          <AvatarBlock displayName={post.author.displayName} avatar={post.author.avatar} className="small" />
        </Link>
        <div className="community-post-meta">
          <div className="community-post-meta-primary">
            <Link className="community-author-name" to={`/community/${post.author.handle}`}>
              {post.author.displayName}
            </Link>
            {post.author.selectedBadge && (() => {
              const badge = getAchievement(post.author.selectedBadge);
              return badge ? (
                <span className="community-author-badge" title={getAchievementTitle(t, badge.id, badge.title)}>{badge.icon}</span>
              ) : null;
            })()}
            {post.author.genreTags?.[0] && (
              <span className="community-genre-dot">· {getGenreDisplayLabel(t, post.author.genreTags[0])}</span>
            )}
          </div>
          <div className="community-post-meta-secondary">
            <span>{formatRelativeTime(t, post.createdAt)}</span>
            {postTypeLabel && <><span className="meta-dot">·</span><span>{postTypeLabel}</span></>}
            {linkedTitle && (
              <><span className="meta-dot">·</span><span>📘 {linkedTitle.length > 22 ? linkedTitle.slice(0, 22) + '…' : linkedTitle}</span></>
            )}
          </div>
        </div>
        {/* Follow button — only for other people's posts, when not already following */}
        {!hideActions && !isOwnPost && onToggleFollow && !isFollowing && (
          <button
            className="btn btn-sm btn-secondary post-follow-btn"
            onClick={() => onToggleFollow(post.author.userId, false)}
          >{t('profile.follow')}</button>
        )}
        {!hideActions && !isOwnPost && onToggleFollow && isFollowing && (
          <button
            className="btn btn-sm btn-ghost post-follow-btn following"
            onClick={() => onToggleFollow(post.author.userId, true)}
          >{t('profile.followingLabel')}</button>
        )}
      </div>

      <div className="community-post-body">
        <p>{post.body}</p>
      </div>

      {post.imageUrl && (
        <div className="community-post-image">
          <img src={post.imageUrl} alt={t("community.postImageAlt")} loading="lazy" />
        </div>
      )}

      {post.audioUrl && (
        <div className="community-post-audio">
          <audio controls preload="none" src={post.audioUrl} />
        </div>
      )}

      {post.excerpt ? (
        <button
          type="button"
          className={`community-excerpt ${post.spoiler ? 'spoiler' : ''}`}
          onClick={() => setRevealSpoiler((v) => !v)}
        >
          <blockquote>{excerptText}</blockquote>
          {post.spoiler && (
            <span className="community-spoiler-toggle">{revealSpoiler ? t('community.hideSpoiler') : t('community.revealSpoiler')}</span>
          )}
          {post.excerpt.caption && <p className="community-excerpt-caption">{post.excerpt.caption}</p>}
        </button>
      ) : null}

      {post.poll ? (
        <PollBlock
          poll={post.poll}
          disabled={!post.viewer}
          onVote={onVotePoll ? (optionId) => onVotePoll(post._id, optionId) : undefined}
        />
      ) : null}

      {!hideActions && (
        <div className="community-post-actions">
          {onToggleLike && (
            <button className={`community-action-btn ${post.viewer?.isLiked ? 'liked' : ''}`} onClick={() => onToggleLike(post)}>
              {post.viewer?.isLiked ? '❤️' : '🤍'} {post.likeCount || 0}
            </button>
          )}
          <button
            className="community-action-btn"
            onClick={() => setShowComments((v) => !v)}
          >
            💬 {post.commentCount || 0} {post.commentCount === 1 ? t('community.commentCount', {count: 1}) : t('community.commentCount_plural', {count: post.commentCount || 0})}
          </button>
          {onToggleSave && (
            <button className="community-action-btn" onClick={() => onToggleSave(post)}>
              {post.viewer?.isSaved ? `🔖 ${t('community.saved')}` : `🔖 ${t('community.savePost')}`}
            </button>
          )}
        </div>
      )}

      {showComments && (
        <InlineComments postId={post._id} viewerProfile={viewerProfile} />
      )}
    </article>
  );
}
